import { type ReactElement, useEffect, useMemo, useRef, useState } from "react";
// eslint-disable-next-line no-restricted-imports -- Dispatching Cognito session thunk in password login handler
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import { LoginLayout } from "../../../containers/enter/commonLogin/loginLayout";
import { APP_V2_APP_EVENTS, logEvent } from "../../lib/analytics";
import { showErrorToast } from "../../lib/Notifications";
import { requestPasswordReset, signInWithPassword } from "../cognito/cognitoPasswordAuth";
import { initCognitoSession, SESSION_INIT_OUTCOME } from "../cognito/initCognitoSession";
import { isSafeRedirectUrl } from "../cognito/isSafeRedirectUrl";
import { mapCognitoError } from "../cognito/mapCognitoError";
import {
  recordLoginAttemptInitiated,
  recordTerminalLoginFailure,
  recordTerminalLoginSuccess,
} from "../loginJourneyTelemetry";
import {
  classifyCognitoError,
  IDENTITY_PROVIDER,
  isNetworkFailure,
  LOGIN_OUTCOME,
  LOGIN_REASON,
  logPasswordLoginFailure,
  logPasswordResetRequestFailure,
  setLoginIdentityProvider,
} from "../loginRum";
import { ForgotPasswordForm } from "./ForgotPasswordForm";
import { LoginForm } from "./LoginForm";
import { ResetEmailSentConfirmation } from "./ResetEmailSentConfirmation";

const DEFAULT_REDIRECT_PATH = "/facility/dashboard";

type PageMode = "login" | "forgotPassword" | "resetEmailSent";

export function CognitoLoginWithPassword(): ReactElement {
  const history = useHistory();
  const location = useLocation();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch = useDispatch<any>();
  const searchParameters = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const requestedUrl = searchParameters.get("requestedUrl");
  const [mode, setMode] = useState<PageMode>("login");
  const modeRef = useRef(mode);
  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [resetEmail, setResetEmail] = useState("");

  const handleLogin = async (email: string, password: string) => {
    setError(undefined);
    setIsSubmitting(true);
    setLoginIdentityProvider(IDENTITY_PROVIDER.COGNITO_PASSWORD);

    try {
      logEvent(APP_V2_APP_EVENTS.COGNITO_PASSWORD_LOGIN_STARTED);
      recordLoginAttemptInitiated({ method: IDENTITY_PROVIDER.COGNITO_PASSWORD, email });
      await signInWithPassword(email, password);
      logEvent(APP_V2_APP_EVENTS.COGNITO_PASSWORD_LOGIN_COGNITO_SUCCESS);

      localStorage.setItem("emailForSignIn", email);
      const target =
        requestedUrl && isSafeRedirectUrl(requestedUrl) ? requestedUrl : DEFAULT_REDIRECT_PATH;

      const sessionResult = await dispatch(initCognitoSession({ authMethod: "non-passkey" }));
      if (sessionResult.outcome !== SESSION_INIT_OUTCOME.HYDRATED) {
        throw sessionResult.error ?? new Error("Failed to hydrate session after password sign-in");
      }

      logEvent(APP_V2_APP_EVENTS.COGNITO_PASSWORD_LOGIN_SUCCESS, {
        outcome: LOGIN_OUTCOME.SUCCESS,
      });
      recordTerminalLoginSuccess();
      history.replace(target);
    } catch (authError: unknown) {
      const mappedError = mapCognitoError(authError, "password");
      const { outcome, reason } =
        mappedError.code === "auth/unknown" && isNetworkFailure(authError)
          ? { outcome: LOGIN_OUTCOME.EXPECTED_ERROR, reason: LOGIN_REASON.NETWORK_FAILURE }
          : classifyCognitoError(mappedError.code);
      recordTerminalLoginFailure();
      logPasswordLoginFailure({
        eventName: APP_V2_APP_EVENTS.COGNITO_PASSWORD_LOGIN_FAILURE,
        error: authError,
        metadata: {
          errorCode: mappedError.code,
          errorMessage: mappedError.message,
          outcome,
          reason,
        },
      });
      if (mappedError.code === "auth/password-reset-required") {
        setError(mappedError.message);
      } else {
        showErrorToast(mappedError.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRequestReset = async (email: string) => {
    setError(undefined);
    setIsSubmitting(true);

    try {
      // No ensureCognitoUser JIT here: a password reset only applies to a user
      // who already exists in Cognito with a password. JIT-creating one would
      // make a duplicate, email_verified=false account whose reset is blocked
      // anyway. JIT belongs in first-login (magic link / OTP), not reset.
      await requestPasswordReset(email);
      logEvent(APP_V2_APP_EVENTS.COGNITO_PASSWORD_RESET_REQUESTED);
      if (modeRef.current !== "forgotPassword") {
        return;
      }
      setResetEmail(email);
      setMode("resetEmailSent");
    } catch (authError: unknown) {
      const mappedError = mapCognitoError(authError, "password");
      logPasswordResetRequestFailure({
        error: authError,
        metadata: { errorCode: mappedError.code, errorMessage: mappedError.message, email },
      });
      if (modeRef.current === "forgotPassword") {
        setError(mappedError.message);
        showErrorToast(mappedError.message);
      }
    } finally {
      if (modeRef.current === "forgotPassword") {
        setIsSubmitting(false);
      }
    }
  };

  const handleBackToLogin = () => {
    setError(undefined);
    setIsSubmitting(false);
    setMode("login");
  };

  return (
    <LoginLayout>
      {mode === "login" && (
        <LoginForm
          error={error}
          isSubmitting={isSubmitting}
          onForgotPassword={() => {
            setError(undefined);
            setMode("forgotPassword");
          }}
          onSubmit={(email, password) => {
            void handleLogin(email, password);
          }}
        />
      )}
      {mode === "forgotPassword" && (
        <ForgotPasswordForm
          error={error}
          isSubmitting={isSubmitting}
          onBackToLogin={handleBackToLogin}
          onSubmit={(email) => {
            void handleRequestReset(email);
          }}
        />
      )}
      {mode === "resetEmailSent" && (
        <ResetEmailSentConfirmation email={resetEmail} onBackToLogin={handleBackToLogin} />
      )}
    </LoginLayout>
  );
}
